import React, { FC, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import AlertTemplate, { AlertTypes } from '@components/Alert/Alert';
import { Icons } from '@components/Icon';
import useTranslations from '@hooks/useTranslation';
import ButtonTemplate from '@ui/Button/Button';
import CheckboxTemplate from '@ui/Checkbox/Checkbox';
import {EMAIL_REGEX, FBQ_EVENTS, MINIMUM_PASSWORD_LENGTH, ROUTES} from '../../constants';
import { authApi } from '../../services/auth.service';
import {
    Agreements,
    Background,
    CheckboxLabel,
    Error,
    GlobalErrorMessage,
    Input,
    InputSection,
    Option,
    Result,
    Select,
    SuccessIcon,
    SuccessMessage,
    Title,
    Wrapper,
} from './Register.elements';
import {hasIncorrectEmail, hasIncorrectPhone} from "@utils/validator.utils";

export const POSITIONS =
    'Advokát | Advokátsky koncipient | Asistent | Asistent audítora | Asistent daňového poradcu | Audítor | Bezpečnostný manažér | Daňový poradca | Dekan | Ekonóm | Ekonomický / Finančný riaditeľ | Exekútor | Exekútorský koncipient | Finančný controller | Finančný manažér | Finančný špecialista | Generálny riaditeľ | Hlavný účtovník | Inšpektor | IT Manažér | IT špecialista | Kontrolór | Krajský prokurátor | Lektor | Majiteľ | Manažér | Manažér kvality | Manažér logistiky | Marketingový manažér | Marketingový riaditeľ | Marketingový špecialista | Mzdový účtovník | Notár | Notársky koncipient | Obchodný manažér | Obchodný riaditeľ | Obchodný zástupca | Okresný prokurátor | Pedagóg | Personalista | Personálny konzultant | Personálny manažér | Personálny riaditeľ | Právnik firemné | Právnik štátny | Prednosta | Predseda | Predseda súdu | Prevádzkový manažér | Primátor | Prodekan | Produktový manažér | Programátor | Štátny zástupca | Prorektor | Referent, administratívny pracovník | Regionálny / Oblastný manažér | Rektor | Riaditeľ | Riaditeľ call centra | Riaditeľ IT | Riaditeľ kvality a ISO | Riaditeľ logistiky | Riaditeľ odboru | Riaditeľ pobočky | Riaditeľ správy súdu | Riaditeľ školy | Správca konkurznej podstaty | Starosta | Sudca | Špecialista bezpečnosti | Špecialista ochrany osobných údajov | Štatutár | Tajomník | Technický manažér | Technický riaditeľ | Technik | Účtovné | Vedúci | Vedúci ekonomického oddelenia | Vedúci právneho oddelenia | Výkonný riaditeľ | Výrobný riaditeľ | Vyšší súdny úradník | Zástupca riaditeľa | Živnostník / SZČO';

export const CZ_STATE_NAME = 'Česká Republika';
export const SK_STATE_NAME = 'Slovensko';

export enum STATES {
    'Slovensko' = 'SK',
    'Česká Republika' = 'CZ',
}

export interface RegisterProps {}

const RegisterTemplate: FC<RegisterProps> = () => {
    const { t } = useTranslations();

    const router = useRouter();

    const [isSent, setIsSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFailed, setIsFailed] = useState(false);
    const [isUserExist, setIsUserExist] = useState(false);

    const [hasInvoiceDetails, setHasInvoiceDetails] = useState(false);
    const [hasBillingDetails, setHasBillingDetails] = useState(false);
    const [hasDeliveryDetails, setHasDeliveryDetails] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [hasEmailError, setHasEmailError] = useState(false);
    const [hasFirstNameError, setHasFirstNameError] = useState(false);
    const [hasLastNameError, setHasLastNameError] = useState(false);
    const [hasPhoneError, setHasPhoneError] = useState(false);
    const [hasPasswordError, setHasPasswordError] = useState(false);
    const [hasPasswordConfirmationError, setHasPasswordConfirmationError] = useState(false);
    const [hasBusinessAddressError, setHasBusinessAddressError] = useState(false);
    const [hasBusinessCityError, setHasBusinessCityError] = useState(false);
    const [hasBusinessPostalCodeError, setHasBusinessPostalCodeError] = useState(false);
    const [hasBusinessCountryError, setHasBusinessCountryError] = useState(false);
    const [hasDeliveryFirstNameError, setHasDeliveryFirstNameError] = useState(false);
    const [hasDeliveryLastNameError, setHasDeliveryLastNameError] = useState(false);
    const [hasDeliveryPhoneError, setHasDeliveryPhoneError] = useState(false);
    const [hasDeliveryAddressError, setHasDeliveryAddressError] = useState(false);
    const [hasDeliveryCityError, setHasDeliveryCityError] = useState(false);
    const [hasDeliveryPostalCodeError, setHasDeliveryPostalCodeError] = useState(false);
    const [hasDeliveryCountryError, setHasDeliveryCountryError] = useState(false);
    const [hasBusinessNameError, setHasBusinessNameError] = useState(false);
    const [hasBusinessIdError, setHasBusinessIdError] = useState(false);
    const [hasBusinessVatIdError, setHasBusinessVatIdError] = useState(false);
    const [hasBusinessVatNoError, setHasBusinessVatNoError] = useState(false);

    const [isBusinessAgreementChecked, setIsBusinessAgreementChecked] = useState(false);
    const [isPersonallyAgreementChecked, setIsPersonallyAgreementChecked] = useState(false);
    const [isMarketingAgreementChecked, setIsMarketingAgreementChecked] = useState(false);

    const emailInputRef = useRef(null);
    const firstNameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const phoneInputRef = useRef(null);
    const positionInputRef = useRef(null);
    const passwordInputRef = useRef(null);
    const passwordConfirmationInputRef = useRef(null);
    const businessAddressInputRef = useRef(null);
    const businessPostalCodeInputRef = useRef(null);
    const businessCityInputRef = useRef(null);
    const businessCountryInputRef = useRef(null);
    const deliveryFirstNameInputRef = useRef(null);
    const deliveryLastNameInputRef = useRef(null);
    const deliveryCompanyInputRef = useRef(null);
    const deliveryPhoneInputRef = useRef(null);
    const deliveryAddressInputRef = useRef(null);
    const deliveryPostalCodeInputRef = useRef(null);
    const deliveryCityInputRef = useRef(null);
    const deliveryCountryInputRef = useRef(null);
    const businessNameInputRef = useRef(null);
    const businessIdInputRef = useRef(null);
    const businessVatIdInputRef = useRef(null);
    const businessVatNoInputRef = useRef(null);

    const emailValidation = (isSubmit?: boolean) => {
        setHasEmailError(false);

        if (hasIncorrectEmail(emailInputRef?.current?.value)) {
            setHasEmailError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const firstNameValidation = (isSubmit?: boolean) => {
        setHasFirstNameError(false);

        if (firstNameInputRef?.current?.value === '') {
            setHasFirstNameError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const lastNameValidation = (isSubmit?: boolean) => {
        setHasLastNameError(false);

        if (lastNameInputRef?.current?.value === '') {
            setHasLastNameError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const phoneValidation = (isSubmit?: boolean) => {
        setHasPhoneError(false);

        if (phoneInputRef?.current?.value === '') {
            setHasPhoneError(true);
            if (isSubmit) setHasError(true);
            return true;
        } else if (phoneInputRef?.current?.value && hasIncorrectPhone(phoneInputRef?.current?.value)) {
            setHasPhoneError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const passwordValidation = (isSubmit?: boolean) => {
        setHasPasswordError(false);

        if (
            passwordInputRef?.current?.value === '' ||
            passwordInputRef?.current?.value?.length < MINIMUM_PASSWORD_LENGTH
        ) {
            setHasPasswordError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const passwordRepeatValidation = (isSubmit?: boolean) => {
        setHasPasswordConfirmationError(false);

        if (passwordInputRef?.current?.value !== passwordConfirmationInputRef?.current?.value) {
            setHasPasswordConfirmationError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const businessAddressValidation = (isSubmit?: boolean) => {
        setHasBusinessAddressError(false);

        if (hasInvoiceDetails && businessAddressInputRef?.current?.value === '') {
            setHasBusinessAddressError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const businessCityValidation = (isSubmit?: boolean) => {
        setHasBusinessCityError(false);

        if (hasInvoiceDetails && businessCityInputRef?.current?.value === '') {
            setHasBusinessCityError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const businessPostalCodeValidation = (isSubmit?: boolean) => {
        setHasBusinessPostalCodeError(false);

        if (hasInvoiceDetails && businessPostalCodeInputRef?.current?.value === '') {
            setHasBusinessPostalCodeError(true);
            if (isSubmit) setHasError(true);
            return true;
        } else if (
            hasInvoiceDetails &&
            businessPostalCodeInputRef?.current?.value &&
            !businessPostalCodeInputRef?.current?.value?.match(/^[0-9]{5}$/)
        ) {
            setHasBusinessPostalCodeError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const businessNameCodeValidation = (isSubmit?: boolean) => {
        setHasBusinessNameError(false);

        if (hasBillingDetails && businessNameInputRef?.current?.value === '') {
            setHasBusinessNameError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const businessIdCodeValidation = (isSubmit?: boolean) => {
        setHasBusinessIdError(false);

        if (hasBillingDetails && businessIdInputRef?.current?.value === '') {
            setHasBusinessIdError(true);
            if (isSubmit) setHasError(true);
            return true;
        } else if (
            hasBillingDetails &&
            businessIdInputRef?.current?.value &&
            !businessIdInputRef?.current?.value?.match(/^[0-9]{8}$/)
        ) {
            setHasBusinessIdError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const businessVatIdCodeValidation = (isSubmit?: boolean) => {
        setHasBusinessVatIdError(false);

        if (businessCountryInputRef?.current?.value === SK_STATE_NAME) {
            if (hasBillingDetails && businessVatIdInputRef?.current?.value === '') {
                setHasBusinessVatIdError(true);
                if (isSubmit) setHasError(true);
                return true;
            } else if (
                hasBillingDetails &&
                businessVatIdInputRef?.current?.value &&
                !businessVatIdInputRef?.current?.value?.match(/^[0-9]{10}$/)
            ) {
                setHasBusinessVatIdError(true);
                if (isSubmit) setHasError(true);
                return true;
            }
        } else if (businessCountryInputRef?.current?.value === CZ_STATE_NAME) {
            if (hasBillingDetails && businessVatIdInputRef?.current?.value === '') {
                setHasBusinessVatIdError(true);
                if (isSubmit) setHasError(true);
                return true;
            } else if (
                hasBillingDetails &&
                businessVatIdInputRef?.current?.value &&
                !businessVatIdInputRef?.current?.value?.match(/^[A-Z]{2}[0-9]{8,10}$/)
            ) {
                setHasBusinessVatIdError(true);
                if (isSubmit) setHasError(true);
                return true;
            }
        }

        return false;
    };

    const businessVatNoCodeValidation = (isSubmit?: boolean) => {
        setHasBusinessVatNoError(false);

        if (businessCountryInputRef?.current?.value === SK_STATE_NAME) {
            if (hasBillingDetails && businessVatNoInputRef?.current?.value === '') {
                setHasBusinessVatNoError(true);
                if (isSubmit) setHasError(true);
                return true;
            } else if (
                hasBillingDetails &&
                businessVatNoInputRef?.current?.value &&
                !businessVatNoInputRef?.current?.value?.match(/^[A-Z]{2}[0-9]{10}$/)
            ) {
                setHasBusinessVatNoError(true);
                if (isSubmit) setHasError(true);
                return true;
            }
        }

        return false;
    };

    const deliveryFirstNameValidation = (isSubmit?: boolean) => {
        setHasDeliveryFirstNameError(false);

        if (hasDeliveryDetails && deliveryFirstNameInputRef?.current?.value === '') {
            setHasDeliveryFirstNameError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const deliveryLastNameValidation = (isSubmit?: boolean) => {
        setHasDeliveryLastNameError(false);

        if (hasDeliveryDetails && deliveryLastNameInputRef?.current?.value === '') {
            setHasDeliveryLastNameError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const deliveryPhoneValidation = (isSubmit?: boolean) => {
        setHasDeliveryPhoneError(false);

        if (hasDeliveryDetails && deliveryPhoneInputRef?.current?.value === '') {
            setHasDeliveryPhoneError(true);
            if (isSubmit) setHasError(true);
            return true;
        } else if (
            hasDeliveryDetails &&
            deliveryPhoneInputRef?.current?.value &&
            hasIncorrectPhone(deliveryPhoneInputRef?.current?.value)
        ) {
            setHasDeliveryPhoneError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const deliveryAddressValidation = (isSubmit?: boolean) => {
        setHasDeliveryAddressError(false);

        if (hasDeliveryDetails && deliveryAddressInputRef?.current?.value === '') {
            setHasDeliveryAddressError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const deliveryCityValidation = (isSubmit?: boolean) => {
        setHasDeliveryCityError(false);

        if (hasDeliveryDetails && deliveryCityInputRef?.current?.value === '') {
            setHasDeliveryCityError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const deliveryPostalCodeValidation = (isSubmit?: boolean) => {
        setHasDeliveryPostalCodeError(false);

        if (hasDeliveryDetails && deliveryPostalCodeInputRef?.current?.value === '') {
            setHasDeliveryPostalCodeError(true);
            if (isSubmit) setHasError(true);
            return true;
        } else if (
            hasDeliveryDetails &&
            deliveryPostalCodeInputRef?.current?.value &&
            !deliveryPostalCodeInputRef?.current?.value?.match(/^[0-9]{5}$/)
        ) {
            setHasDeliveryPostalCodeError(true);
            if (isSubmit) setHasError(true);
            return true;
        }

        return false;
    };

    const handleSubmitClick = async () => {
        setHasError(false);
        setIsFailed(false);
        setIsUserExist(false);
        setHasEmailError(false);
        setHasFirstNameError(false);
        setHasLastNameError(false);
        setHasPhoneError(false);
        setHasPasswordError(false);
        setHasPasswordConfirmationError(false);
        setHasBusinessAddressError(false);
        setHasBusinessCityError(false);
        setHasBusinessPostalCodeError(false);
        setHasBusinessCountryError(false);
        setHasBusinessNameError(false);
        setHasBusinessIdError(false);
        setHasBusinessVatIdError(false);
        setHasBusinessVatNoError(false);
        setHasDeliveryFirstNameError(false);
        setHasDeliveryLastNameError(false);
        setHasDeliveryPhoneError(false);
        setHasDeliveryAddressError(false);
        setHasDeliveryCityError(false);
        setHasDeliveryPostalCodeError(false);
        setHasDeliveryCountryError(false);

        let hasValidationError = false;

        if (emailValidation(true)) hasValidationError = true;

        if (firstNameValidation(true)) hasValidationError = true;

        if (lastNameValidation(true)) hasValidationError = true;

        if (phoneValidation(true)) hasValidationError = true;

        if (passwordValidation(true)) hasValidationError = true;

        if (passwordRepeatValidation(true)) hasValidationError = true;

        if (businessAddressValidation(true)) hasValidationError = true;

        if (businessCityValidation(true)) hasValidationError = true;

        if (businessPostalCodeValidation(true)) hasValidationError = true;

        if (businessNameCodeValidation(true)) hasValidationError = true;

        if (businessIdCodeValidation(true)) hasValidationError = true;

        if (businessVatIdCodeValidation(true)) hasValidationError = true;

        if (businessVatNoCodeValidation(true)) hasValidationError = true;

        if (businessVatNoCodeValidation(true)) hasValidationError = true;

        if (deliveryFirstNameValidation(true)) hasValidationError = true;

        if (deliveryLastNameValidation(true)) hasValidationError = true;

        if (deliveryPhoneValidation(true)) hasValidationError = true;

        if (deliveryAddressValidation(true)) hasValidationError = true;

        if (deliveryCityValidation(true)) hasValidationError = true;

        if (deliveryPostalCodeValidation(true)) hasValidationError = true;

        if (hasValidationError) return;

        try {
            setIsLoading(true);
            await authApi.register({
                email: emailInputRef?.current?.value,
                password: passwordInputRef?.current?.value,
                name: firstNameInputRef?.current?.value,
                surname: lastNameInputRef?.current?.value,
                phone_number: phoneInputRef?.current?.value,
                ...(positionInputRef?.current?.value &&
                    POSITIONS.split('|').indexOf(positionInputRef?.current?.value) !== -1 && {
                        position: POSITIONS.split('|').indexOf(positionInputRef?.current?.value),
                    }),
                use_invoice_details: hasInvoiceDetails,
                marketing_agreement: isMarketingAgreementChecked,
                ...(hasInvoiceDetails && {
                    invoice: {
                        street: businessAddressInputRef?.current?.value,
                        city: businessCityInputRef?.current?.value,
                        zip_code: businessPostalCodeInputRef?.current?.value,
                        state: (STATES as { [key: string]: string })[businessCountryInputRef?.current?.value],
                    },
                }),
                use_company_details: hasBillingDetails,
                ...(hasBillingDetails && {
                    company: {
                        name: businessNameInputRef?.current?.value,
                        business_id: businessIdInputRef?.current?.value,
                        ...(businessVatIdInputRef?.current?.value !== '' && {
                            tax_id: businessVatIdInputRef?.current?.value,
                        }),
                        ...(businessVatNoInputRef?.current?.value !== '' && {
                            vat_no: businessVatNoInputRef?.current?.value,
                        }),
                    },
                }),
                use_delivery_address: hasDeliveryDetails,
                ...(hasDeliveryDetails && {
                    delivery: {
                        name: deliveryFirstNameInputRef?.current?.value,
                        surname: deliveryLastNameInputRef?.current?.value,
                        phone_number: deliveryPhoneInputRef?.current?.value,
                        ...(deliveryCompanyInputRef?.current?.value !== '' && {
                            company: deliveryCompanyInputRef?.current?.value,
                        }),
                        street: deliveryAddressInputRef?.current?.value,
                        city: deliveryCityInputRef?.current?.value,
                        zip_code: deliveryPostalCodeInputRef?.current?.value,
                        state: (STATES as { [key: string]: string })[deliveryCountryInputRef?.current?.value],
                    },
                }),
            });

            (window as any)?.fbq?.('track', FBQ_EVENTS.COMPLETE_REGISTRATION);

            setIsLoading(false);
            setIsSent(true);
        } catch (error: any) {
            setIsLoading(false);

            if (error?.response?.status === 409) {
                setIsUserExist(true);
            } else {
                setIsFailed(true);
            }
        }
    };

    return (
        <Wrapper>
            {isSent ? (
                <Result>
                    <SuccessIcon name={Icons.Send} />
                    <SuccessMessage>{t('modal.register.alert.sent')}</SuccessMessage>
                </Result>
            ) : (
                <>
                    {isFailed && <AlertTemplate type={AlertTypes.error} message={t('modal.register.alert.failed')} />}
                    {isUserExist && <AlertTemplate type={AlertTypes.error} message={t('modal.register.alert.used')} />}
                    <Title>{t('modal.register.title')}</Title>
                    <InputSection>
                        <Input
                            className={hasEmailError ? 'error' : ''}
                            ref={emailInputRef}
                            type="email"
                            name="email"
                            placeholder={`${t('shop.input.mail')}*`}
                            onKeyUp={() => emailValidation()}
                        />
                        {hasEmailError && <Error>{t('modal.register.mail.error')}</Error>}
                    </InputSection>
                    <InputSection>
                        <Input
                            className={hasFirstNameError ? 'error' : ''}
                            ref={firstNameInputRef}
                            type="firstName"
                            name="firstName"
                            placeholder={`${t('shop.input.firstName')}*`}
                            onKeyUp={() => firstNameValidation()}
                        />
                        {hasFirstNameError && <Error>{t('modal.register.firstName.error')}</Error>}
                    </InputSection>
                    <InputSection>
                        <Input
                            className={hasLastNameError ? 'error' : ''}
                            type="lastName"
                            name="lastName"
                            ref={lastNameInputRef}
                            placeholder={`${t('shop.input.lastName')}*`}
                            onKeyUp={() => lastNameValidation()}
                        />
                        {hasLastNameError && <Error>{t('modal.register.lastName.error')}</Error>}
                    </InputSection>
                    <InputSection>
                        <Input
                            className={hasPhoneError ? 'error' : ''}
                            type="phone"
                            name="phone"
                            ref={phoneInputRef}
                            placeholder={`${t('shop.input.phone')}*`}
                            onKeyUp={() => phoneValidation()}
                        />
                        {hasPhoneError && <Error>{t('modal.register.phone.error')}</Error>}
                    </InputSection>
                    <Select ref={positionInputRef} name="positions">
                        <option>{t('modal.register.position.placeholder')}</option>
                        {POSITIONS.split('|').map((value, key) => (
                            <Option key={key} value={value}>
                                {value}
                            </Option>
                        ))}
                    </Select>
                    <InputSection>
                        <Input
                            className={hasPasswordError ? 'error' : ''}
                            ref={passwordInputRef}
                            type="password"
                            autoComplete="off"
                            placeholder={`${t('shop.input.password')}*`}
                            onKeyUp={() => passwordValidation()}
                        />
                        {hasPasswordError && <Error>{t('modal.register.password.error')}</Error>}
                    </InputSection>
                    <InputSection>
                        <Input
                            className={hasPasswordConfirmationError ? 'error' : ''}
                            ref={passwordConfirmationInputRef}
                            type="password"
                            placeholder={`${t('shop.input.passwordConfirmation')}*`}
                            onKeyUp={() => passwordRepeatValidation()}
                        />
                        {hasPasswordConfirmationError && (
                            <Error>{t('modal.register.passwordConfirmation.error')}</Error>
                        )}
                    </InputSection>
                    <Background isActive={hasInvoiceDetails} marginTop={20}>
                        <CheckboxTemplate
                            name="invoice"
                            checked={hasInvoiceDetails}
                            onChange={() => setHasInvoiceDetails(!hasInvoiceDetails)}
                        >
                            <CheckboxLabel dangerouslySetInnerHTML={{ __html: t('modal.register.checkbox1') }} />
                        </CheckboxTemplate>
                        {hasInvoiceDetails && (
                            <>
                                <InputSection>
                                    <Input
                                        className={hasBusinessAddressError ? 'error' : ''}
                                        ref={businessAddressInputRef}
                                        type="address"
                                        name="address"
                                        placeholder={`${t('shop.input.address')}*`}
                                        onKeyUp={() => businessAddressValidation()}
                                    />
                                    {hasBusinessAddressError && (
                                        <Error bottom={5}>{t('modal.register.address.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasBusinessCityError ? 'error' : ''}
                                        type="city"
                                        name="city"
                                        ref={businessCityInputRef}
                                        placeholder={`${t('shop.input.city')}*`}
                                        onKeyUp={() => businessCityValidation()}
                                    />
                                    {hasBusinessCityError && <Error bottom={5}>{t('modal.register.city.error')}</Error>}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasBusinessPostalCodeError ? 'error' : ''}
                                        type="postalCode"
                                        name="postalCode"
                                        ref={businessPostalCodeInputRef}
                                        placeholder={`${t('shop.input.postalCode')}*`}
                                        onKeyUp={() => businessPostalCodeValidation()}
                                    />
                                    {hasBusinessPostalCodeError && (
                                        <Error bottom={5}>{t('modal.register.postalCode.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Select bottom={30} top={10} ref={businessCountryInputRef} name="businessCountry">
                                        {Object.keys(STATES).map((value, key) => (
                                            <Option key={key} value={value}>
                                                {value}
                                            </Option>
                                        ))}
                                    </Select>
                                    {hasBusinessCountryError && (
                                        <Error bottom={2}>{t('modal.register.country.error')}</Error>
                                    )}
                                </InputSection>
                                <CheckboxTemplate
                                    name="billing"
                                    checked={hasBillingDetails}
                                    onChange={() => setHasBillingDetails(!hasBillingDetails)}
                                >
                                    <CheckboxLabel
                                        dangerouslySetInnerHTML={{ __html: t('modal.register.checkbox2') }}
                                    />
                                </CheckboxTemplate>
                                {hasBillingDetails && (
                                    <>
                                        <InputSection>
                                            <Input
                                                className={hasBusinessNameError ? 'error' : ''}
                                                ref={businessNameInputRef}
                                                placeholder={`${t('shop.input.company.name')}*`}
                                                onKeyUp={() => businessNameCodeValidation()}
                                            />
                                            {hasBusinessNameError && (
                                                <Error bottom={5}>{t('general.fillCorrectShape')}</Error>
                                            )}
                                        </InputSection>
                                        <InputSection>
                                            <Input
                                                className={hasBusinessIdError ? 'error' : ''}
                                                ref={businessIdInputRef}
                                                placeholder={`${t('shop.input.company.id')}*`}
                                                onKeyUp={() => businessIdCodeValidation()}
                                            />
                                            {hasBusinessIdError && (
                                                <Error bottom={5}>{t('general.fillCorrectShape')}</Error>
                                            )}
                                        </InputSection>
                                        <InputSection>
                                            <Input
                                                className={hasBusinessVatIdError ? 'error' : ''}
                                                ref={businessVatIdInputRef}
                                                placeholder={`${t('shop.input.company.vatId')}*`}
                                                onKeyUp={() => businessVatIdCodeValidation()}
                                            />
                                            {hasBusinessVatIdError && (
                                                <Error bottom={5}>{t('general.fillCorrectShape')}</Error>
                                            )}
                                        </InputSection>
                                        {businessCountryInputRef?.current?.value === SK_STATE_NAME && (
                                            <InputSection>
                                                <Input
                                                    className={hasBusinessVatNoError ? 'error' : ''}
                                                    ref={businessVatNoInputRef}
                                                    placeholder={`${t('shop.input.company.vatNo')}*`}
                                                    onKeyUp={() => businessVatNoCodeValidation()}
                                                />
                                                {hasBusinessVatNoError && (
                                                    <Error bottom={5}>{t('general.fillCorrectShape')}</Error>
                                                )}
                                            </InputSection>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Background>
                    <Background isActive={hasDeliveryDetails}>
                        <CheckboxTemplate
                            name="company"
                            checked={hasDeliveryDetails}
                            onChange={() => setHasDeliveryDetails(!hasDeliveryDetails)}
                        >
                            <CheckboxLabel dangerouslySetInnerHTML={{ __html: t('modal.register.checkbox3') }} />
                        </CheckboxTemplate>
                        {hasDeliveryDetails && (
                            <>
                                <InputSection>
                                    <Input
                                        className={hasDeliveryFirstNameError ? 'error' : ''}
                                        ref={deliveryFirstNameInputRef}
                                        type="firstName"
                                        name="firstName"
                                        placeholder={`${t('shop.input.firstName')}*`}
                                        onKeyUp={() => deliveryFirstNameValidation()}
                                    />
                                    {hasDeliveryFirstNameError && (
                                        <Error bottom={5}>{t('modal.register.firstName.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasDeliveryLastNameError ? 'error' : ''}
                                        ref={deliveryLastNameInputRef}
                                        type="lastName"
                                        name="lastName"
                                        placeholder={`${t('shop.input.lastName')}*`}
                                        onKeyUp={() => deliveryLastNameValidation()}
                                    />
                                    {hasDeliveryLastNameError && (
                                        <Error bottom={5}>{t('modal.register.lastName.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Input ref={deliveryCompanyInputRef} placeholder={t('shop.input.company.name')} />
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasDeliveryPhoneError ? 'error' : ''}
                                        ref={deliveryPhoneInputRef}
                                        type="phone"
                                        name="phone"
                                        placeholder={`${t('shop.input.phone')}*`}
                                        onKeyUp={() => deliveryPhoneValidation()}
                                    />
                                    {hasDeliveryPhoneError && (
                                        <Error bottom={5}>{t('modal.register.phone.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasDeliveryAddressError ? 'error' : ''}
                                        ref={deliveryAddressInputRef}
                                        type="address"
                                        name="address"
                                        placeholder={`${t('shop.input.address')}*`}
                                        onKeyUp={() => deliveryAddressValidation()}
                                    />
                                    {hasDeliveryAddressError && (
                                        <Error bottom={5}>{t('modal.register.address.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasDeliveryCityError ? 'error' : ''}
                                        ref={deliveryCityInputRef}
                                        type="city"
                                        name="city"
                                        placeholder={`${t('shop.input.city')}*`}
                                        onKeyUp={() => deliveryCityValidation()}
                                    />
                                    {hasDeliveryCityError && <Error bottom={5}>{t('modal.register.city.error')}</Error>}
                                </InputSection>
                                <InputSection>
                                    <Input
                                        className={hasDeliveryPostalCodeError ? 'error' : ''}
                                        ref={deliveryPostalCodeInputRef}
                                        type="postalCode"
                                        name="postalCode"
                                        placeholder={`${t('shop.input.postalCode')}*`}
                                        onKeyUp={() => deliveryPostalCodeValidation()}
                                    />
                                    {hasDeliveryPostalCodeError && (
                                        <Error bottom={5}>{t('modal.register.postalCode.error')}</Error>
                                    )}
                                </InputSection>
                                <InputSection>
                                    <Select bottom={30} top={10} ref={deliveryCountryInputRef} name="deliveryCountry">
                                        {Object.keys(STATES).map((value, key) => (
                                            <Option key={key} value={value}>
                                                {value}
                                            </Option>
                                        ))}
                                    </Select>
                                    {hasDeliveryCountryError && (
                                        <Error bottom={5}>{t('modal.register.country.error')}</Error>
                                    )}
                                </InputSection>
                            </>
                        )}
                    </Background>
                    <Agreements>
                        <CheckboxTemplate
                            name="bussiness"
                            checked={isBusinessAgreementChecked}
                            onChange={() => setIsBusinessAgreementChecked(!isBusinessAgreementChecked)}
                        >
                            <CheckboxLabel>
                                Súhlasím s{' '}
                                <a
                                    onClick={() => {
                                        window.open(ROUTES.CONDITIONS, '_blank');
                                        setIsBusinessAgreementChecked(!isBusinessAgreementChecked);
                                    }}
                                >
                                    obchodnými podmienkami*
                                </a>
                            </CheckboxLabel>
                        </CheckboxTemplate>
                        <CheckboxTemplate
                            name="personally"
                            checked={isPersonallyAgreementChecked}
                            onChange={() => setIsPersonallyAgreementChecked(!isPersonallyAgreementChecked)}
                        >
                            <CheckboxLabel>
                                Súhlasím so{' '}
                                <a
                                    onClick={() => {
                                        window.open(ROUTES.GDPR, '_blank');
                                        setIsPersonallyAgreementChecked(!isPersonallyAgreementChecked);
                                    }}
                                >
                                    spracovaním osobných údajov*
                                </a>
                            </CheckboxLabel>
                        </CheckboxTemplate>
                        <CheckboxTemplate
                            name="marketing"
                            checked={isMarketingAgreementChecked}
                            onChange={() => setIsMarketingAgreementChecked(!isMarketingAgreementChecked)}
                        >
                            <CheckboxLabel>
                                Chcem dostávať upozornenia na{' '}
                                <a
                                    onClick={() => {
                                        window.open(`${ROUTES.GDPR}#marketing`, '_blank');
                                        setIsMarketingAgreementChecked(!isMarketingAgreementChecked);
                                    }}
                                >
                                    novinky, zľavy a bonusy
                                </a>
                            </CheckboxLabel>
                        </CheckboxTemplate>
                    </Agreements>
                    {(hasEmailError ||
                        hasFirstNameError ||
                        hasLastNameError ||
                        hasPhoneError ||
                        hasPasswordError ||
                        hasPasswordConfirmationError ||
                        hasBusinessAddressError ||
                        hasBusinessCityError ||
                        hasBusinessPostalCodeError ||
                        hasBusinessCountryError ||
                        hasDeliveryFirstNameError ||
                        hasDeliveryLastNameError ||
                        hasDeliveryPhoneError ||
                        hasDeliveryAddressError ||
                        hasDeliveryCityError ||
                        hasDeliveryPostalCodeError ||
                        hasDeliveryCountryError ||
                        hasBusinessNameError ||
                        hasBusinessIdError ||
                        hasBusinessVatIdError ||
                        hasBusinessVatNoError) && <GlobalErrorMessage>{t('contact.form.error')}</GlobalErrorMessage>}
                    <ButtonTemplate
                        isLoading={isLoading}
                        onClick={handleSubmitClick}
                        disabled={!isBusinessAgreementChecked || !isPersonallyAgreementChecked}
                        text={t('modal.register.submit')}
                    />
                </>
            )}
        </Wrapper>
    );
};

export default RegisterTemplate;
